import { PublicClientApplication } from "@azure/msal-browser";
import AUTH_CONFIG from "../config/auth.js";
import { EventBus } from './event-bus.js';

// Use centralized auth configuration
const OUTLOOK_CLIENT_ID = AUTH_CONFIG.OUTLOOK_CLIENT_ID;
const TOPS2_CLIENT_ID = AUTH_CONFIG.TOPS2_CLIENT_ID;
const GRAPH_SCOPES = AUTH_CONFIG.GRAPH_SCOPES;
const TOPS2_SCOPES = AUTH_CONFIG.TOPS2_SCOPES;

// Environment URL configuration
const ENV_URLS = AUTH_CONFIG.ENV_URLS;

// Get the current environment from the hostname
function getCurrentEnvironment() {
    return AUTH_CONFIG.getCurrentEnvironment();
}

// Get the redirect URI for the current environment
function getRedirectUri() {
    return AUTH_CONFIG.getRedirectUri();
}

// Storage keys for caching
const STORAGE_KEYS = {
    GRAPH_TOKEN: 'graph_auth_token',
    GRAPH_EXPIRY: 'graph_token_expiry',
    TOPS2_TOKEN: 'tops2_auth_token',
    TOPS2_EXPIRY: 'tops2_token_expiry',
    ACCOUNT: 'msal_account'
};

// Debug logging helper
const debugLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    console.log(`[TokenService ${timestamp}] ${message}`);
    if (data) {
        console.log('Data:', data);
    }
};

const msalConfig = {
    auth: {
        clientId: OUTLOOK_CLIENT_ID,
        authority: AUTH_CONFIG.AUTHORITY,
        redirectUri: getRedirectUri(),
        navigateToLoginRequestUrl: true
    }
};

let msalInstance;
let initializePromise = null;
let interactionInProgress = false;
let interactionPromise = null;

async function initialize() {
    if (!initializePromise) {
        initializePromise = (async () => {
            try {
                msalInstance = new PublicClientApplication(msalConfig);
                await msalInstance.initialize();
                
                // Set active account if available
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                    debugLog(`Initialized with active account: ${accounts[0].username}`);
                } else {
                    debugLog("No accounts available during initialization");
                }
                
                console.log("MSAL PublicClientApplication initialized.");
            } catch (error) {
                console.error("Failed to initialize MSAL instance:", error);
                initializePromise = null;
                throw error;
            }
        })();
    }
    return initializePromise;
}

async function ensureInitialized() {
    if (!msalInstance) {
        console.warn("MSAL instance is not initialized. Initializing now...");
        await initialize();
    }
}

async function cleanupExistingPopups() {
    if (msalInstance) {
        try {
            await msalInstance.removeEventCallback();
            await msalInstance.clearCache();
            interactionInProgress = false;
            interactionPromise = null;
        } catch (error) {
            console.warn("Error cleaning up popups:", error);
        }
    }
}

async function acquireTokenWithLock(acquireTokenFn) {
    if (interactionInProgress) {
        console.warn("Interaction in progress, waiting for completion...");
        if (interactionPromise) {
            return await interactionPromise;
        }
        EventBus.emit('show-notification', {
            type: 'error',
            message: 'Authentication interaction in progress but no promise available',
            icon: 'ErrorIcon.80x80',
            persistence: true
        });
        return null;
    }

    interactionInProgress = true;
    interactionPromise = (async () => {
        try {
            return await acquireTokenFn();
        } finally {
            interactionInProgress = false;
            interactionPromise = null;
        }
    })();

    return await interactionPromise;
}

// Storage helpers
function saveToStorage(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        debugLog(`Saved to storage: ${key}`);
        return true;
    } catch (error) {
        console.error(`Failed to save to storage: ${key}`, error);
        return false;
    }
}

function getFromStorage(key) {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (error) {
        console.error(`Failed to get from storage: ${key}`, error);
        return null;
    }
}

function clearStorage() {
    Object.values(STORAGE_KEYS).forEach(key => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error(`Failed to remove from storage: ${key}`, error);
        }
    });
}

// Token management
function cacheToken(token, tokenType, account, expiresIn = AUTH_CONFIG.TOKEN_EXPIRY.DEFAULT_EXPIRY_SECONDS) {
    const expiry = Date.now() + (expiresIn * 1000);
    
    if (tokenType === 'graph') {
        saveToStorage(STORAGE_KEYS.GRAPH_TOKEN, token);
        saveToStorage(STORAGE_KEYS.GRAPH_EXPIRY, expiry);
    } else if (tokenType === 'tops2') {
        saveToStorage(STORAGE_KEYS.TOPS2_TOKEN, token);
        saveToStorage(STORAGE_KEYS.TOPS2_EXPIRY, expiry);
    }
    
    if (account) {
        saveToStorage(STORAGE_KEYS.ACCOUNT, account);
    }
    debugLog(`${tokenType} token cached successfully`);
}

function getCachedToken(tokenType) {
    const tokenKey = tokenType === 'graph' ? STORAGE_KEYS.GRAPH_TOKEN : STORAGE_KEYS.TOPS2_TOKEN;
    const expiryKey = tokenType === 'graph' ? STORAGE_KEYS.GRAPH_EXPIRY : STORAGE_KEYS.TOPS2_EXPIRY;
    
    const token = getFromStorage(tokenKey);
    const expiry = getFromStorage(expiryKey);
    
    if (!token || !expiry) {
        debugLog(`No cached ${tokenType} token found`);
        return null;
    }

    // Add 5 minute buffer to prevent using tokens that are about to expire
    const bufferTime = AUTH_CONFIG.TOKEN_EXPIRY.REFRESH_BUFFER_MINUTES * 60 * 1000; // Convert minutes to milliseconds
    if (Date.now() >= (expiry - bufferTime)) {
        debugLog(`Cached ${tokenType} token is expired or expiring soon`);
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(expiryKey);
        return null;
    }

    debugLog(`Retrieved valid cached ${tokenType} token`);
    return token;
}

function getCachedAccount() {
    return getFromStorage(STORAGE_KEYS.ACCOUNT);
}

async function getToken(scopes = TOPS2_SCOPES) {
    await ensureInitialized();
    
    // Determine token type based on scopes
    const isGraphToken = scopes.some(scope => GRAPH_SCOPES.includes(scope));
    const tokenType = isGraphToken ? 'graph' : 'tops2';
    
    // First, try to get a cached token
    const cachedToken = getCachedToken(tokenType);
    if (cachedToken) {
        debugLog(`Using cached ${tokenType} token`);
        return cachedToken;
    }

    debugLog(`No valid cached ${tokenType} token, acquiring new token...`);
    
    // Get the current account
    const accounts = msalInstance.getAllAccounts();
    let account = accounts.length > 0 ? accounts[0] : null;
    
    // If no account found, try to get from storage
    if (!account) {
        account = getCachedAccount();
    }

    // Set the active account if we have one
    if (account && accounts.length > 0) {
        msalInstance.setActiveAccount(account);
        debugLog(`Set active account: ${account.username}`);
    }

    const tokenRequest = {
        scopes: scopes,
        forceRefresh: false
    };

    // Only add account to request if we have one
    if (account) {
        tokenRequest.account = account;
    }

    return await acquireTokenWithLock(async () => {
        try {
            // If we don't have an account, we need to login first
            if (!account) {
                debugLog(`No account available for ${tokenType}, forcing login...`);
                return await forceLoginPopup(scopes);
            }

            debugLog(`Attempting silent token acquisition for ${tokenType}...`, { scopes });
            
            // Try silent acquisition first
            let silentResult;
            try {
                silentResult = await msalInstance.acquireTokenSilent(tokenRequest);
            } catch (silentError) {
                debugLog(`Silent ${tokenType} token acquisition failed, trying with forceRefresh:`, silentError.message);
                
                // If silent fails, try with force refresh
                tokenRequest.forceRefresh = true;
                try {
                    silentResult = await msalInstance.acquireTokenSilent(tokenRequest);
                } catch (refreshError) {
                    debugLog(`Force refresh failed for ${tokenType}:`, refreshError.message);
                    throw refreshError;
                }
            }
            
            if (silentResult && silentResult.accessToken) {
                debugLog(`Silent ${tokenType} token acquisition successful`);
                
                // Set the active account from the result
                if (silentResult.account) {
                    msalInstance.setActiveAccount(silentResult.account);
                }
                
                cacheToken(silentResult.accessToken, tokenType, silentResult.account, silentResult.expiresOn ? Math.floor((silentResult.expiresOn.getTime() - Date.now()) / 1000) : AUTH_CONFIG.TOKEN_EXPIRY.DEFAULT_EXPIRY_SECONDS);
                return silentResult.accessToken;
            }
        } catch (silentError) {
            debugLog(`Silent ${tokenType} token acquisition failed:`, silentError.message);
            
            if (silentError.name === "InteractionRequiredAuthError" || 
                silentError.errorCode === "interaction_required" ||
                silentError.errorCode === "consent_required" ||
                silentError.errorCode === "no_account_error" ||
                silentError.errorCode === "token_renewal_error") {
                
                debugLog(`User interaction required for ${tokenType}, attempting popup...`);
                
                try {
                    const popupResult = await msalInstance.acquireTokenPopup(tokenRequest);
                    
                    if (popupResult && popupResult.accessToken) {
                        debugLog(`Popup ${tokenType} token acquisition successful`);
                        
                        // Set the active account from the result
                        if (popupResult.account) {
                            msalInstance.setActiveAccount(popupResult.account);
                        }
                        
                        cacheToken(popupResult.accessToken, tokenType, popupResult.account, popupResult.expiresOn ? Math.floor((popupResult.expiresOn.getTime() - Date.now()) / 1000) : AUTH_CONFIG.TOKEN_EXPIRY.DEFAULT_EXPIRY_SECONDS);
                        return popupResult.accessToken;
                    }
                } catch (popupError) {
                    debugLog(`Popup ${tokenType} token acquisition failed:`, popupError.message);
                    
                    // If popup fails, try force login
                    debugLog(`Trying force login for ${tokenType}...`);
                    return await forceLoginPopup(scopes);
                }
            } else {
                EventBus.emit('show-notification', {
                    type: 'error',
                    message: `Failed to acquire ${tokenType} token: ${silentError.message}`,
                    icon: 'ErrorIcon.80x80',
                    persistence: true
                });
                return null;
            }
        }
        
        EventBus.emit('show-notification', {
            type: 'error',
            message: `Failed to acquire ${tokenType} token`,
            icon: 'ErrorIcon.80x80',
            persistence: true
        });
        return null;
    });
}

// Separate methods for getting specific token types
async function getGraphToken() {
    return await getToken(GRAPH_SCOPES);
}

async function getTops2Token() {
    return await getToken(TOPS2_SCOPES);
}

async function forceLoginPopup(scopes) {
    await cleanupExistingPopups();
    return await acquireTokenWithLock(async () => {
        debugLog('Starting force login popup...', { scopes });
        
        const loginResponse = await msalInstance.loginPopup({
            scopes,
            prompt: 'login'
        });
        
        // Set the active account immediately after login
        if (loginResponse.account) {
            msalInstance.setActiveAccount(loginResponse.account);
            debugLog(`Set active account after login: ${loginResponse.account.username}`);
        }
        
        // Determine token type
        const isGraphToken = scopes.some(scope => GRAPH_SCOPES.includes(scope));
        const tokenType = isGraphToken ? 'graph' : 'tops2';
        
        // Cache the token from the popup login
        cacheToken(
            loginResponse.accessToken,
            tokenType,
            loginResponse.account,
            loginResponse.expiresIn
        );
        
        debugLog(`Force login successful for ${tokenType}`);
        return loginResponse.accessToken;
    });
}

class TokenService {
    static async initialize() {
        await initialize();
    }

    static async getToken(scopes) {
        await initialize();
        return await getToken(scopes);
    }

    static async getGraphToken() {
        await initialize();
        return await getGraphToken();
    }

    static async getTops2Token() {
        await initialize();
        return await getTops2Token();
    }

    static async clearCache() {
        await initialize();
        try {
            msalInstance.getTokenCache().clear();
            clearStorage();
            debugLog('All token caches cleared');
        } catch (error) {
            console.error('Failed to clear token caches:', error);
            throw error;
        }
    }

    static async getCurrentAccount() {
        await initialize();
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) return activeAccount;
        
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
            return accounts[0];
        }
        
        return null;
    }

    static async silentSignIn() {
        try {
            debugLog('Attempting silent sign-in');
            const cachedToken = getCachedToken('tops2');
            if (cachedToken) {
                debugLog('Using cached token for silent sign-in');
                return cachedToken;
            }

            await ensureInitialized();
            const token = await getTops2Token();
            if (!token) {
                EventBus.emit('show-notification', {
                    type: 'error',
                    message: 'Silent sign-in failed - no token acquired',
                    icon: 'ErrorIcon.80x80',
                    persistence: true
                });
                return null;
            }
            return token;
        } catch (error) {
            debugLog('Silent sign-in failed', error);
            throw error;
        }
    }

    static async ensureToken(tokenType = 'tops2') {
        try {
            const token = tokenType === 'graph' ? await this.getGraphToken() : await this.getTops2Token();
            if (!token) {
                EventBus.emit('show-notification', {
                    type: 'error',
                    message: 'No token available',
                    icon: 'ErrorIcon.80x80',
                    persistence: true
                });
                return null;
            }
            return token;
        } catch (error) {
            console.error('Token acquisition failed:', error);
            
            // If token acquisition fails, try clearing cache and retry once
            if (error.message.includes('interaction_required') || 
                error.message.includes('token_renewal_error') ||
                error.message.includes('expired')) {
                
                debugLog('Token error detected, clearing cache and retrying...');
                await this.clearCache();
                
                // Retry once after clearing cache
                try {
                    const token = tokenType === 'graph' ? await this.getGraphToken() : await this.getTops2Token();
                    if (!token) {
                        EventBus.emit('show-notification', {
                            type: 'error',
                            message: 'No token available after retry',
                            icon: 'ErrorIcon.80x80',
                            persistence: true
                        });
                        return null;
                    }
                    return token;
                } catch (retryError) {
                    console.error('Token acquisition failed even after cache clear:', retryError);
                    throw retryError;
                }
            }
            
            throw error;
        }
    }

    static async refreshToken(tokenType = 'tops2') {
        debugLog(`Attempting to refresh ${tokenType} token...`);
        
        try {
            await initialize();
            
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                EventBus.emit('show-notification', {
                    type: 'error',
                    message: 'No accounts available for token refresh',
                    icon: 'ErrorIcon.80x80',
                    persistence: true
                });
                return null;
            }
            
            const account = accounts[0];
            msalInstance.setActiveAccount(account);
            
            const scopes = tokenType === 'graph' ? GRAPH_SCOPES : TOPS2_SCOPES;
            const tokenRequest = {
                scopes: scopes,
                account: account,
                forceRefresh: true // Force refresh to get new token
            };
            
            const result = await msalInstance.acquireTokenSilent(tokenRequest);
            
            if (result && result.accessToken) {
                debugLog(`${tokenType} token refresh successful`);
                cacheToken(
                    result.accessToken, 
                    tokenType, 
                    result.account, 
                    result.expiresOn ? Math.floor((result.expiresOn.getTime() - Date.now()) / 1000) : AUTH_CONFIG.TOKEN_EXPIRY.DEFAULT_EXPIRY_SECONDS
                );
                return result.accessToken;
            }
            
            EventBus.emit('show-notification', {
                type: 'error',
                message: 'Token refresh failed - no access token in response',
                icon: 'ErrorIcon.80x80',
                persistence: true
            });
            return null;
            
        } catch (error) {
            debugLog(`${tokenType} token refresh failed:`, error.message);
            throw error;
        }
    }

    static async getAuthHeaders(tokenType = 'tops2') {
        const token = await this.ensureToken(tokenType);
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    static async callApi(url, method = 'GET', body = null, tokenType = 'tops2') {
        const headers = await this.getAuthHeaders(tokenType);
        const options = {
            method,
            headers,
            body: body ? JSON.stringify(body) : null
        };

        const response = await fetch(url, options);
        if (!response.ok) {
            EventBus.emit('show-notification', {
                type: 'error',
                message: `API call failed: ${response.status}`,
                icon: 'ErrorIcon.80x80',
                persistence: true
            });
            return null;
        }
        return await response.json();
    }

    static async testRefreshTokens() {
        debugLog('Testing refresh token functionality...');
        
        try {
            // Clear all cached tokens
            await this.clearCache();
            debugLog('Cache cleared');
            
            // Test TOPS2 token refresh
            debugLog('Testing TOPS2 token acquisition...');
            const tops2Token = await this.getTops2Token();
            debugLog('TOPS2 token acquired successfully');
            
            // Test Graph token refresh  
            debugLog('Testing Graph token acquisition...');
            const graphToken = await this.getGraphToken();
            debugLog('Graph token acquired successfully');
            
            return {
                success: true,
                tops2Token: tops2Token ? 'acquired' : 'failed',
                graphToken: graphToken ? 'acquired' : 'failed'
            };
        } catch (error) {
            debugLog('Token test failed:', error.message);
            return {
                success: false,
                error: error.message
            };
        }
    }

    static async forceTokenRefresh(tokenType = 'both') {
        debugLog(`Force refreshing ${tokenType} tokens...`);
        
        try {
            const results = {};
            
            if (tokenType === 'tops2' || tokenType === 'both') {
                debugLog('Force refreshing TOPS2 token...');
                results.tops2 = await this.refreshToken('tops2');
            }
            
            if (tokenType === 'graph' || tokenType === 'both') {
                debugLog('Force refreshing Graph token...');
                results.graph = await this.refreshToken('graph');
            }
            
            return results;
        } catch (error) {
            debugLog('Force refresh failed:', error.message);
            throw error;
        }
    }
}

export default TokenService;
export { getGraphToken, getTops2Token, GRAPH_SCOPES, TOPS2_SCOPES };